import { getMetricDefinition, type MetricId } from './registry'

/**
 * A measurement as exchanged between clients and the Worker. Timestamps are ISO
 * strings; `values` are SI numbers keyed by the metric's field keys; a non-null
 * `deletedAt` marks a tombstone whose values may be omitted.
 */
export type MeasurementEntry = {
   entryId: string
   metricId: MetricId
   timestamp: string
   values: Record<string, number>
   deletedAt: string | null
   updatedAt: string
}

export type MeasurementValidation =
   { ok: true; entry: MeasurementEntry } | { ok: false; error: string }

/** Client-generated ids: UUIDs or similar opaque tokens. */
export const ENTRY_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/
/** How far ahead of the server clock a timestamp may sit before it counts as future. */
const CLOCK_SKEW_TOLERANCE_MS = 5 * 60 * 1000
/** Tombstones older than this can be dropped from sync responses and storage. */
export const MEASUREMENT_TOMBSTONE_RETENTION_MS = 90 * 24 * 60 * 60 * 1000

/**
 * Parses an ISO timestamp, returning epoch ms or null.
 *
 * @param value - The candidate value.
 * @returns Epoch ms, or null when not a valid ISO string.
 */
function parseIsoMs(value: unknown): number | null {
   if (typeof value !== 'string' || !value) {
      return null
   }
   const ms = Date.parse(value)
   return Number.isFinite(ms) ? ms : null
}

/**
 * Normalizes any parseable timestamp to a canonical ISO string.
 *
 * @param value - The candidate value.
 * @returns The ISO string, or null when invalid.
 */
function toCanonicalIso(value: unknown): string | null {
   const ms = parseIsoMs(value)
   return ms === null ? null : new Date(ms).toISOString()
}

/**
 * Validates the `values` object against the metric's fields: required keys
 * present, every value a finite positive number, no unknown keys.
 *
 * @param metricId - The metric the values belong to.
 * @param raw - The candidate values object.
 * @returns The normalized values, or an error message.
 */
function validateValues(
   metricId: MetricId,
   raw: unknown,
): { ok: true; values: Record<string, number> } | { ok: false; error: string } {
   const definition = getMetricDefinition(metricId)!
   const record = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
   const knownKeys = new Set(definition.fields.map(field => field.key))
   const values: Record<string, number> = {}

   for (const key of Object.keys(record)) {
      if (!knownKeys.has(key)) {
         return { ok: false, error: `values.${key} is not a field of ${metricId}.` }
      }
   }
   for (const field of definition.fields) {
      const value = record[field.key]
      if (value === undefined || value === null) {
         if (field.required) {
            return { ok: false, error: `values.${field.key} is required.` }
         }
         continue
      }
      if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
         return { ok: false, error: `values.${field.key} must be a positive number.` }
      }
      values[field.key] = value
   }
   return { ok: true, values }
}

type EntryTimestamps = { timestamp: string; updatedAt: string; deletedAt: string | null }

/**
 * Validates the three timestamps of an entry: `timestamp` (not in the future),
 * `updatedAt`, and the optional `deletedAt`.
 *
 * @param raw - The candidate entry fields.
 * @param now - Epoch ms used for the future check.
 * @returns Canonical ISO timestamps, or an error message.
 */
function validateTimestamps(
   raw: Record<string, unknown>,
   now: number,
): { ok: true; times: EntryTimestamps } | { ok: false; error: string } {
   const timestampMs = parseIsoMs(raw.timestamp)
   if (timestampMs === null) {
      return { ok: false, error: 'timestamp must be an ISO date-time.' }
   }
   if (timestampMs > now + CLOCK_SKEW_TOLERANCE_MS) {
      return { ok: false, error: 'timestamp cannot be in the future.' }
   }
   const updatedAt = toCanonicalIso(raw.updatedAt)
   if (updatedAt === null) {
      return { ok: false, error: 'updatedAt must be an ISO date-time.' }
   }
   let deletedAt: string | null = null
   if (raw.deletedAt !== undefined && raw.deletedAt !== null) {
      deletedAt = toCanonicalIso(raw.deletedAt)
      if (deletedAt === null) {
         return { ok: false, error: 'deletedAt must be null or an ISO date-time.' }
      }
   }
   return {
      ok: true,
      times: { timestamp: new Date(timestampMs).toISOString(), updatedAt, deletedAt },
   }
}

/**
 * Validates and normalizes a measurement entry from an untrusted source. Both
 * the Worker (request bodies) and the web app (storage, sync responses) use it.
 *
 * @param input - The candidate entry.
 * @param options - `now` (epoch ms) for the future-timestamp check; defaults to `Date.now()`.
 * @returns The normalized entry, or an error message.
 */
export function validateMeasurementEntry(
   input: unknown,
   options: { now?: number } = {},
): MeasurementValidation {
   if (!input || typeof input !== 'object') {
      return { ok: false, error: 'entry must be an object.' }
   }
   const raw = input as Record<string, unknown>

   if (typeof raw.entryId !== 'string' || !ENTRY_ID_PATTERN.test(raw.entryId)) {
      return { ok: false, error: 'entryId must be 8–64 characters of [A-Za-z0-9_-].' }
   }
   const definition = getMetricDefinition(raw.metricId)
   if (!definition) {
      return { ok: false, error: `metricId must be one of the registered metrics.` }
   }
   const timestamps = validateTimestamps(raw, options.now ?? Date.now())
   if (!timestamps.ok) {
      return timestamps
   }

   // Tombstones carry no values; live entries must validate against the registry.
   let values: Record<string, number> = {}
   if (timestamps.times.deletedAt === null) {
      const checked = validateValues(definition.id, raw.values)
      if (!checked.ok) {
         return checked
      }
      values = checked.values
   }

   return {
      ok: true,
      entry: {
         entryId: raw.entryId,
         metricId: definition.id,
         values,
         ...timestamps.times,
      },
   }
}

/**
 * Decides whether an incoming entry replaces the stored one: last writer wins on
 * `updatedAt`, and on a tie a tombstone beats a live entry so deletes converge.
 *
 * @param existing - The stored entry, or null when new.
 * @param incoming - The candidate entry.
 * @returns True when `incoming` should be stored.
 */
export function incomingWins(
   existing: Pick<MeasurementEntry, 'updatedAt' | 'deletedAt'> | null,
   incoming: Pick<MeasurementEntry, 'updatedAt' | 'deletedAt'>,
): boolean {
   if (!existing) {
      return true
   }
   const existingMs = Date.parse(existing.updatedAt)
   const incomingMs = Date.parse(incoming.updatedAt)
   if (incomingMs !== existingMs) {
      return incomingMs > existingMs
   }
   return incoming.deletedAt !== null && existing.deletedAt === null
}

/**
 * Merges two entry collections keyed by `entryId` using {@link incomingWins}.
 * Tombstones older than the retention window are dropped from the result.
 *
 * @param local - The current entries.
 * @param remote - The entries to merge in.
 * @param options - `now` (epoch ms) for tombstone expiry; defaults to `Date.now()`.
 * @returns The merged entries, sorted newest timestamp first.
 */
export function mergeMeasurementEntries(
   local: readonly MeasurementEntry[],
   remote: readonly MeasurementEntry[],
   options: { now?: number } = {},
): MeasurementEntry[] {
   const now = options.now ?? Date.now()
   const byId = new Map(local.map(entry => [entry.entryId, entry]))
   for (const entry of remote) {
      if (incomingWins(byId.get(entry.entryId) ?? null, entry)) {
         byId.set(entry.entryId, entry)
      }
   }
   return [...byId.values()]
      .filter(entry => !isExpiredTombstone(entry, now))
      .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp))
}

/**
 * Returns whether an entry is a tombstone past the retention window.
 *
 * @param entry - The entry.
 * @param now - Epoch ms.
 * @returns True when the tombstone can be dropped.
 */
export function isExpiredTombstone(entry: MeasurementEntry, now: number): boolean {
   return (
      entry.deletedAt !== null &&
      now - Date.parse(entry.deletedAt) > MEASUREMENT_TOMBSTONE_RETENTION_MS
   )
}
